import React, { useEffect, useState } from 'react';
import {
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonCard,
  IonCardHeader,
  IonCardContent,
  IonAvatar,
  IonButton,
  IonBadge,
  IonText,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonActionSheet,
  IonIcon,
  IonImg,
  IonSearchbar,
  IonList,
  IonItem
} from '@ionic/react';
import {
  heartOutline,
  chatbubbleOutline,
  shareSocialOutline,
  ellipsisHorizontal
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';

import './SocialFeed.css';
import { SearchUsersResponseDto, PostDto } from '../api';
import useAstroApiClient from '../hooks/useAstroApiClient';
import UserProfile from './UserProfile';

interface User {
  id: string;
  name: string;
  avatar: string;
  sign: string;
  level: number;
  badge: string;
  isFollowing?: boolean;
}

interface Post {
  id: string;
  user: User;
  content: string;
  timestamp: string;
  likes: number;
  comments: number;
  shares: number;
  tags: string[];
}

const SocialFeed: React.FC = () => {
  const api = useAstroApiClient();
  const { t } = useTranslation();
  const [feedType, setFeedType] = useState<'public' | 'following'>('public');
  const [posts, setPosts] = useState<Post[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [actionPostId, setActionPostId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchUsersResponseDto[]>([]);
  const [selectedUser, setSelectedUser] = useState<SearchUsersResponseDto | null>(null);

  const loadPosts = async (reset = false) => {
    const nextPage = reset ? 1 : page;

    try {
      const raw: PostDto[] = await api.apiSocialFeedMe();
      const newPosts: Post[] = (raw || []).map(p => ({
        id: p.id ?? '',
        user: {
          id: p.userId ?? '',
          name: t('socialUserFallback', { id: p.userId ?? '' }),
          avatar: p.imageUrl ?? '',
          sign: '',
          level: 1,
          badge: '',
          isFollowing: false,
        },
        content: p.caption ?? '',
        timestamp: p.createdAt ? p.createdAt.toISOString() : '',
        likes: 0,
        comments: 0,
        shares: 0,
        tags: [],
      }));

      setPosts(prev => (reset ? newPosts : [...prev, ...newPosts]));
      setPage(nextPage + 1);

      if (newPosts.length === 0) {
        setHasMore(false);
      }
    } catch {
      setHasMore(false);
    }
  };

  const handleSearch = async (value: string) => {
    setQuery(value);

    if (!value) {
      setResults([]);
      return;
    }

    try {
      const users = await api.apiSocialSearch(value);
      setResults(users || []);
    } catch {
      setResults([]);
    }
  };

  useEffect(() => {
    setPosts([]);
    setPage(1);
    setHasMore(true);
    loadPosts(true);
    // eslint-disable-next-line
  }, [feedType]);

  const toggleFollow = async (user: User) => {
    try {
      if (user.isFollowing) {
        await api.apiSocialFollowMeDelete(user.id);
      } else {
        await api.apiSocialFollowMePost(user.id);
      }

      setPosts(prev =>
        prev.map(p =>
          p.user.id === user.id
            ? { ...p, user: { ...p.user, isFollowing: !p.user.isFollowing } }
            : p
        )
      );
    } catch {
      // Ignore errors for now
    }
  };

  const handleHide = async (postId: string) => {
    try {
      await api.adminSocialReportsHide(postId);
    } finally {
      setPosts(prev => prev.filter(p => p.id !== postId));
    }
  };

  const handleReport = async (postId: string) => {
    try {
      await api.apiSocialPostsReportMe(postId, undefined);
    } finally {
      setPosts(prev => prev.filter(p => p.id !== postId));
    }
  };

  const actionButtons = [
    {
      text: t('hide'),
      handler: () => {
        if (actionPostId != null) handleHide(actionPostId);
      }
    },
    {
      text: t('report'),
      role: 'destructive' as const,
      handler: () => {
        if (actionPostId != null) handleReport(actionPostId);
      }
    },
    {
      text: t('cancel'),
      role: 'cancel' as const
    }
  ];

  if (selectedUser) {
    return (
      <UserProfile
        userId={selectedUser.id!}
        onBack={() => {
          setSelectedUser(null);
          setQuery('');
          setResults([]);
        }}
      />
    );
  }

  return (
    <div className="social-feed">
      <div className="search-bar">
        <IonSearchbar
          value={query}
          onIonChange={e => handleSearch(e.detail.value!)}
          placeholder={t('searchUserPlaceholder')}
        />
      </div>

      {results.length > 0 && (
        <IonList>
          {results.map(r => (
            <IonItem
              key={r.id}
              button
              onClick={() => setSelectedUser(r)}
            >
              <IonLabel>
                <h2>{r.name}</h2>
                <p>@{r.publicId}</p>
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
      )}

      <IonSegment
        value={feedType}
        onIonChange={e => setFeedType(e.detail.value as 'public' | 'following')}
      >
        <IonSegmentButton value="public">
          <IonLabel>{t('public')}</IonLabel>
        </IonSegmentButton>
        <IonSegmentButton value="following">
          <IonLabel>{t('following')}</IonLabel>
        </IonSegmentButton>
      </IonSegment>

      {posts
        .filter(p => (feedType === 'public' ? true : p.user.isFollowing))
        .map(post => (
          <IonCard key={post.id}>
            <IonCardHeader>
              <div className="post-header">
                <IonAvatar>
                  <IonImg src={post.user.avatar} />
                </IonAvatar>
                <div className="post-user">
                  <IonText className="post-name">{post.user.name}</IonText>
                  <IonBadge color="danger">{post.user.sign}</IonBadge>
                  <IonText color="medium" className="post-meta">
                    {t('level', { level: post.user.level })} • {post.timestamp}
                  </IonText>
                </div>
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={() => toggleFollow(post.user)}
                >
                  {post.user.isFollowing ? t('unfollow') : t('follow')}
                </IonButton>
                <IonButton
                  fill="clear"
                  size="small"
                  onClick={() => setActionPostId(post.id)}
                >
                  <IonIcon icon={ellipsisHorizontal} />
                </IonButton>
              </div>
            </IonCardHeader>
            <IonCardContent>
              <p>{post.content}</p>
              <div className="actions">
                <IonButton fill="clear" size="small">
                  <IonIcon icon={heartOutline} slot="start" />
                  {post.likes}
                </IonButton>
                <IonButton fill="clear" size="small">
                  <IonIcon icon={chatbubbleOutline} slot="start" />
                  {post.comments}
                </IonButton>
                <IonButton fill="clear" size="small">
                  <IonIcon icon={shareSocialOutline} slot="start" />
                  {post.shares}
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        ))}

      <IonInfiniteScroll
        onIonInfinite={async e => {
          await loadPosts();
          (e.target as HTMLIonInfiniteScrollElement).complete();
        }}
        disabled={!hasMore}
      >
        <IonInfiniteScrollContent loadingSpinner="bubbles" />
      </IonInfiniteScroll>

      <IonActionSheet
        isOpen={actionPostId !== null}
        onDidDismiss={() => setActionPostId(null)}
        buttons={actionButtons}
      />
    </div>
  );
};

export default SocialFeed;
